'use client';

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Zap, TrendingUp, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { EnergyDataPoint } from '@/types/energy';

interface HistoricDevelopmentChartMobileProps {
  data: EnergyDataPoint[];
  gemeindeName: string;
}

const HistoricDevelopmentChartMobile: React.FC<HistoricDevelopmentChartMobileProps> = ({ data, gemeindeName }) => {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  const energySources = [
    { key: 'wasser', name: 'Wasser', color: '#3B82F6' },
    { key: 'sonne', name: 'Sonne', color: '#F59E0B' },
    { key: 'biomasse', name: 'Biomasse', color: '#10B981' },
    { key: 'biogas', name: 'Biogas', color: '#8B5CF6' },
  ];

  const formatValue = (value: number) => {
    return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-sm mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {formatValue(entry.value)} kW
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-4"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <TrendingUp className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Entwicklung</h3>
          <p className="text-xs text-gray-600">2019 - 2023</p>
        </div>
      </div>

      {/* Energy Source Selector */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedSource(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
            selectedSource === null
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          Alle
        </button>
        {energySources.map((source) => (
          <button
            key={source.key}
            onClick={() => setSelectedSource(source.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              selectedSource === source.key
                ? 'text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
            style={{
              backgroundColor: selectedSource === source.key ? source.color : undefined,
            }}
          >
            {source.name}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="jahr" 
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => value.toString().slice(2)}
            />
            <YAxis 
              tick={{ fontSize: 11 }}
              tickFormatter={formatValue}
            />
            <Tooltip content={<CustomTooltip />} />
            {energySources.map((source) => (
              <Line
                key={source.key}
                type="monotone"
                dataKey={source.key}
                stroke={source.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                hide={selectedSource !== null && selectedSource !== source.key}
                animationDuration={500}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        {energySources.map((source) => (
          <div
            key={source.key}
            className={`flex items-center gap-2 text-xs ${
              selectedSource !== null && selectedSource !== source.key ? 'opacity-50' : ''
            }`}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: source.color }}
            />
            <span className="text-gray-700">{source.name}</span>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-600">Gesamtleistung 2023</p>
            <p className="font-bold text-lg text-gray-900">
              {formatValue(data[data.length - 1]?.gesamt || 0)} kW
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Wachstum seit 2019</p>
            <p className="font-bold text-lg text-green-600">
              +{((((data[data.length - 1]?.gesamt || 0) - (data[0]?.gesamt || 0)) / (data[0]?.gesamt || 1)) * 100).toFixed(0)}%
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HistoricDevelopmentChartMobile;